import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Flex, Form, Input, Typography } from 'antd';

import { useAuth } from '../hooks/useAuth';
import { DashboardLoginError } from '../api/auth';

interface LoginFormValues {
  email: string;
  password: string;
}

const DEFAULT_REDIRECT = '/dashboard';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [form] = Form.useForm<LoginFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const redirectTarget = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      const redirect = params.get('redirect');
      if (redirect && redirect.startsWith('/')) {
        return redirect;
      }
    } catch (_error) {
      // ignore malformed query
    }
    return DEFAULT_REDIRECT;
  }, [location.search]);

  const handleSubmit = async (values: LoginFormValues) => {
    setSubmitting(true);
    setErrorMessage(null);

    try {
      await login({ email: values.email.trim(), password: values.password });
      navigate(redirectTarget, { replace: true });
    } catch (error) {
      if (error instanceof DashboardLoginError) {
        setErrorMessage(error.message);
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('登录失败，请稍后再试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex vertical justify="center" align="center" style={{ minHeight: '100vh' }}>
      <Card style={{ width: 360 }} title="短信额度监控后台登录">
        <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
          <Form.Item
            label="邮箱"
            name="email"
            rules={[{ required: true, message: '请输入邮箱' }, { type: 'email', message: '邮箱格式不正确' }]}
          >
            <Input type="email" placeholder="ops@example.com" allowClear disabled={submitting} />
          </Form.Item>
          <Form.Item
            label="密码"
            name="password"
            rules={[{ required: true, message: '请输入密码' }, { min: 8, message: '密码长度至少 8 位' }]}
          >
            <Input.Password placeholder="请输入密码" disabled={submitting} />
          </Form.Item>

          {errorMessage ? (
            <Form.Item>
              <Alert type="error" message={errorMessage} showIcon />
            </Form.Item>
          ) : null}

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={submitting}>
              登录
            </Button>
          </Form.Item>
        </Form>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          登录后可查看短信额度、执行调账与连接测试。异常请联系运维轮值人员。
        </Typography.Paragraph>
      </Card>
    </Flex>
  );
};

export default LoginPage;
